import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import { requiredString } from '@/utils/zod'

import type { IdAndName } from '../types/id-and-name'

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    display_name: requiredString(),
    academic_level: z
      .object({
        id: requiredString(),
        name: requiredString()
      })
      .nullable(),
    tutor: z
      .object({
        id: requiredString(),
        full_name: requiredString(),
        first_name: requiredString(),
        last_name: requiredString()
      })
      .nullable()
      .optional(),
    preferred_room: z
      .object({
        id: requiredString(),
        name: requiredString()
      })
      .nullable()
  })
)

export type Class = {
  id: string
  name: string
  academicLevel?: IdAndName
  tutor?: {
    id: string
    fullName: string
  }
  preferredRoom?: IdAndName
}

const parseResponse = (response: z.infer<typeof responseSchema>): Class[] =>
  response.map(item => ({
    id: item.id,
    name: item.display_name,
    academicLevel: item.academic_level || undefined,
    tutor: item.tutor
      ? {
          id: item.tutor?.id,
          fullName: item.tutor?.full_name
        }
      : undefined,
    preferredRoom: item.preferred_room || undefined
  }))

const useClassesOptions = () =>
  useQuery({
    queryKey: ['panelClassesList'],
    staleTime: 60 * 100,
    queryFn: async () => {
      const response = await httpService.panel.panelClassesList({
        // @ts-expect-error Waits for backend fix
        page_size: 'max',
        fetchKeys: {
          id: true,
          display_name: true,
          tutor: true,
          academic_level: true,
          preferred_room: true
        }
      })

      return parseResponse(responseSchema.parse(response.results))
    }
  })

export default useClassesOptions
